import React, { useState } from "react";
import { Save, X } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import { Row } from "./ProfileTab";
import VocationTrackPanel from "./VocationTrackPanel";
import { EditToggle, ErrorLine, GhostButton, GoldButton, inputCls, labelCls as fieldLabel } from "./ui";

const READINESS = ["not_ready", "vocational_prep", "job_searching", "work_ready", "employed"];
const toForm = (v) => ({ employer: v.employer || "", trade_track: v.trade_track || "", certifications: (v.certifications || []).join(", "),
  verified_career_placement: !!v.verified_career_placement, readiness_status: v.readiness?.status || "", readiness_notes: v.readiness?.notes || "" });

function VocationForm({ resident, v, onDone, onCancel }) {
  const [f, setF] = useState(toForm(v));
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setBusy(true); setErr(null);
    try { await reApi.patch(`/residents/${resident.id}/vocation`, { ...f, certifications: f.certifications.split(",") }); onDone(); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const opts = [...new Set([...READINESS, f.readiness_status].filter(Boolean))];
  return (
    <div data-testid="vocation-form" className="space-y-3">
      <div className="flex justify-end gap-2">
        <GhostButton data-testid="vocation-cancel" onClick={onCancel}><X className="h-3 w-3" /> Cancel</GhostButton>
        <GoldButton data-testid="vocation-save" onClick={save} disabled={busy}><Save className="h-3 w-3" /> Save</GoldButton>
      </div>
      <ErrorLine testId="vocation-error">{err}</ErrorLine>
      <div className="grid sm:grid-cols-2 gap-3">
        <label><span className={fieldLabel}>Employer</span><input data-testid="vocation-input-employer" className={inputCls} value={f.employer} onChange={set("employer")} /></label>
        <label><span className={fieldLabel}>Trade track</span><input data-testid="vocation-input-track" className={inputCls} value={f.trade_track} onChange={set("trade_track")} /></label>
        <label className="sm:col-span-2"><span className={fieldLabel}>Certifications (comma separated)</span><input data-testid="vocation-input-certs" className={inputCls} value={f.certifications} onChange={set("certifications")} /></label>
        <label><span className={fieldLabel}>Readiness</span>
          <select data-testid="vocation-input-readiness" className={inputCls} value={f.readiness_status} onChange={set("readiness_status")}>
            <option value="">—</option>{opts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select></label>
        <label><span className={fieldLabel}>Readiness notes</span><input data-testid="vocation-input-readiness-notes" className={inputCls} value={f.readiness_notes} onChange={set("readiness_notes")} /></label>
        <label className="flex items-center gap-2 text-sm text-nex-silver"><input type="checkbox" data-testid="vocation-input-verified" checked={f.verified_career_placement} onChange={set("verified_career_placement")} /> Verified career placement</label>
      </div>
    </div>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const words = (s) => (s ? String(s).replace(/_/g, " ") : "—");
const DOT = { green: "bg-emerald-400", yellow: "bg-amber-400", red: "bg-red-400" };
const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted mb-2";

const Sprint = ({ phases }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {Object.entries(phases || {}).map(([phase, items]) => (
      <div key={phase} data-testid={`vocation-${phase}`} className="border border-white/10 p-3">
        <div className={labelCls}>{words(phase)}</div>
        {Object.entries(items).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-xs text-nex-silver py-0.5">
            <span className={`h-2 w-2 rounded-full ${DOT[v] || "bg-white/30"}`} />{words(k)}<span className="ml-auto text-nex-muted">{v}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const DAY = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const t12 = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

const Schedules = ({ weeks, count }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="vocation-schedules-section">
      <button data-testid="vocation-schedules-toggle" onClick={() => setOpen((o) => !o)}
        className="text-sm text-nex-silver hover:text-white transition-colors">
        {count} weekly schedules on file · {open ? "Hide times ▲" : "Show times ▼"}
      </button>
      {open && (
        <div data-testid="vocation-schedules-list" className="mt-2 space-y-2">
          {(weeks || []).map((w) => (
            <div key={w.week} data-testid={`vocation-week-${w.week}`} className="border border-white/10 p-2">
              <div className={labelCls}>Week of {w.week}</div>
              {w.days.length ? w.days.map((d) => (
                <div key={d.day} className="text-xs text-nex-silver py-0.5">
                  {DAY[d.day]} · {t12(d.start)}–{t12(d.end)} · {d.employer || "—"}
                </div>
              )) : <div className="text-xs text-nex-muted">No shifts scheduled</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function VocationTab({ resident, onChanged }) {
  const [editing, setEditing] = useState(false);
  const v = resident.vocation || {};
  if (editing) return <VocationForm resident={resident} v={v} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); onChanged?.(); }} />;
  if (!resident.vocation) return <div className="space-y-3"><EditToggle editing={false} setEditing={setEditing} label="Edit vocation" testId="vocation" /><VocationTrackPanel residentId={resident.id} onChanged={onChanged} /><p data-testid="vocation-empty" className="text-sm text-nex-muted">No vocation record yet.</p></div>;
  return (
    <div data-testid="vocation-tab" className="space-y-5">
      <EditToggle editing={false} setEditing={setEditing} label="Edit vocation" testId="vocation" />
      <VocationTrackPanel residentId={resident.id} onChanged={onChanged} />
      <p className="text-[11px] text-nex-muted">{v.imported_at ? `Imported from the old platform ${fmt(v.imported_at)}` : ""}{v.edited_at ? ` · last edited ${fmt(v.edited_at)} by ${v.edited_by?.name || "—"}` : ""}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Row label="Trade track" testId="vocation-track">{words(v.trade_track)}</Row>
        <Row label="Employer" testId="vocation-employer">{v.employer}</Row>
        <Row label="Certifications" testId="vocation-certs">{(v.certifications || []).join(", ")}</Row>
        <Row label="Verified career placement" testId="vocation-verified">{v.verified_career_placement ? "Yes" : "No"}</Row>
        <Row label="Readiness" testId="vocation-readiness">{`${words(v.readiness?.status)}${v.readiness?.notes ? ` — “${v.readiness.notes}”` : ""} (${v.readiness?.updated_by || "—"}, ${fmt(v.readiness?.updated_at)})`}</Row>
        <Row label="Work-ready" testId="vocation-work-ready">{`${v.work_ready?.value ? "Yes" : "No"} (set ${fmt(v.work_ready?.set_at)} by ${v.work_ready?.set_by || "—"})`}</Row>
        <Row label="Vocational sprint" testId="vocation-sprint-status">{`${words(v.sprint?.status)} · ${v.sprint?.days_idle ?? "—"} days idle · last green ${fmt(v.sprint?.last_green)}`}</Row>
      </div>
      <Schedules weeks={v.schedules} count={v.schedule_records} />
      <Sprint phases={v.sprint?.phases} />
      {[["Placements", v.placements, (p) => `${words(p.vocational_track)} · ${words(p.status)} · ${p.placement_partner || "—"}`],
        ["Jobs", v.jobs, (j) => `${j.title} · ${j.employer || "—"} · $${j.hourly_rate}/hr · ${j.status} · since ${fmt(j.started_at)}`]].map(([title, list, line]) => (
        <div key={title} data-testid={`vocation-${title.toLowerCase()}`}>
          <div className={labelCls}>{title}</div>
          {(list || []).length ? list.map((x, i) => <div key={i} className="text-sm text-nex-silver py-1 border-b border-white/5">{line(x)}</div>)
            : <div className="text-sm text-nex-muted">None</div>}
        </div>
      ))}
    </div>
  );
}
